import { injectPostgresEndpoints } from "./postgresApi";
import { Certificate, Feature, type EntitlementsData } from "../../types/newtypes";
import { withCompletionIdentity, type CompletionIdentity } from "./completionRequest";

export type CourseCertificateStatus = "checking" | "ready" | "generating" | "failed" | "unavailable" | "unknown" | "not-entitled";

export interface CourseCertificateResult {
  status: CourseCertificateStatus;
  certificateId?: string;
}

export const postgresCertificatesApi = injectPostgresEndpoints({
  endpoints: (builder) => ({
    resolveCourseCertificate: builder.mutation<CourseCertificateResult, { masterCourseId: string; identity: CompletionIdentity }>({
      queryFn: ({ masterCourseId, identity }, api) => withCompletionIdentity<CourseCertificateResult>(identity, api, async (request) => {
        const entitlement = await request("/users/me/entitlements");
        if (entitlement.error) {return entitlement;}
        const access = entitlement.data as EntitlementsData;
        if (!Array.isArray(access?.features)) {return { error: { status: "CUSTOM_ERROR", error: "Certificate access could not be confirmed" } };}
        if (access.isSubscriptionExpired || !access.features.includes(Feature.COURSE_CERTIFICATES)) {return { data: { status: "not-entitled" } };}
        const result = await request({ url: "/certificates/issue-master-course", method: "POST", body: { master_course_id: masterCourseId } });
        if (result.error) {return result;}
        const issued = result.data as { success?: boolean; certificate?: { id: string; scope: string; master_course_id: string; user_id: string } | null };
        if (issued?.success === false && issued.certificate === null) {return { data: { status: "unavailable" } };}
        const certificate = issued?.certificate;
        if (issued?.success !== true || !certificate?.id || certificate.scope !== "course" ||
          certificate.master_course_id !== masterCourseId || certificate.user_id !== identity.userId) {
          return { error: { status: "CUSTOM_ERROR", error: "Course certificate could not be confirmed" } };
        }
        const detail = await request(`/certificates/${certificate.id}`);
        if (detail.error) {return detail;}
        const value = detail.data as Certificate;
        if (value?.id !== certificate.id || !["ready", "generating", "failed"].includes(value.pdf_status || "") ||
          (value.pdf_status === "ready" && !value.download_uri)) {
          return { error: { status: "CUSTOM_ERROR", error: "Certificate generation status could not be confirmed" } };
        }
        return { data: { status: value.pdf_status!, certificateId: certificate.id } };
      }),
      invalidatesTags: ["Certificates", { type: "Certificates", id: "LIST" }],
    }),
    // Get user certificates
    getCertificates: builder.query<Certificate[], void>({
      query: () => ({
        url: "/certificates",
        method: "GET",
      }),
      providesTags: ["Certificates"],
    }),

    // Get single certificate by ID - Real API endpoint
    getCertificateById: builder.query<Certificate, string>({
      query: (certificateId) => ({
        url: `/certificate/${certificateId}`,
        method: "GET",
      }),
      providesTags: (result, error, certificateId) => [
        { type: "Certificates", id: certificateId },
      ],
    }),
  }),
});

export const { useGetCertificatesQuery, useGetCertificateByIdQuery, useResolveCourseCertificateMutation } =
  postgresCertificatesApi;
